/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-restricted-syntax */
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ky from 'ky';
import {
  PromptCollection,
  Prompt,
  GenericComment,
  QuestionResponse,
  VideoResponse,
  AudioResponse,
  ImageResponse,
} from '../@types';
import * as Constants from '../constants';
import { api, ApiResponse, throwError } from './BaseApi';
import { SONAR_CACHE_KEYS } from './SonarApi';
import { PROMPT_CACHE } from './PromptApi';
// import { videoPrompts } from '../demo';

const cache = {
  PROMPT_RESPONSES: 'promptResponses',
  USER_RESPONSES: 'userResponse',
  RESPONSE_COMMENTS: 'responseComments',
};
export { cache as RESPONSE_CACHE };

const getResponsesByPromptId = async (
  promptId: string
): Promise<[QuestionResponse[]]> => {
  try {
    const res: any = await api.get(`responses/${promptId}`);
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem loading responses.');
  }
};

export function usePromptResponses(promptId: string) {
  return useQuery(
    [cache.PROMPT_RESPONSES, promptId],
    () => getResponsesByPromptId(promptId),
    {
      keepPreviousData: true,
      enabled: !!promptId,
    }
  );
}

const getUserResponsesByPromptId = async (
  promptId: string,
  userId: string
): Promise<QuestionResponse[]> => {
  try {
    const res: any = await api.post('user_responses', {
      json: {
        user_id: userId,
        prompt_id: promptId,
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response.responses;
  } catch (err) {
    return throwError(err, 'There was a problem loading your responses.');
  }
};

export function useUserPromptResponses(promptId: string, userId: string) {
  return useQuery(
    [cache.USER_RESPONSES, { prompt_id: promptId, user_id: userId }],
    async () => {
      const myResponses = await getUserResponsesByPromptId(promptId, userId);
      return (
        myResponses && {
          [Constants.RESPONSE_TYPE_IDS.AUDIO]: myResponses.find(
            ({ response_type }) =>
              response_type === Constants.RESPONSE_TYPE_IDS.AUDIO
          ) as AudioResponse | undefined,
          [Constants.RESPONSE_TYPE_IDS.VIDEO]: myResponses.find(
            ({ response_type }) =>
              response_type === Constants.RESPONSE_TYPE_IDS.VIDEO
          ) as VideoResponse | undefined,
          [Constants.RESPONSE_TYPE_IDS.TEXT]: myResponses.find(
            ({ response_type }) =>
              response_type === Constants.RESPONSE_TYPE_IDS.TEXT
          ),
          [Constants.RESPONSE_TYPE_IDS.IMAGE]: myResponses.find(
            ({ response_type }) =>
              response_type === Constants.RESPONSE_TYPE_IDS.IMAGE
          ) as ImageResponse | undefined,
          [Constants.RESPONSE_TYPE_IDS.DRAW]: myResponses.find(
            ({ response_type }) =>
              response_type === Constants.RESPONSE_TYPE_IDS.DRAW
          ),
        }
      );
    },
    {
      enabled: !!promptId && !!userId,
    }
  );
}

const getCommentsByResponseId = async (responseId: string | null) => {
  const comments: GenericComment[] = !!responseId
    ? await api.get(`response/${responseId}/comments`).json()
    : [];
  return comments;
};
export function useResponseComments(responseId: string | null) {
  return useQuery(
    [cache.RESPONSE_COMMENTS, responseId],
    () => getCommentsByResponseId(responseId),
    {
      enabled: !!responseId,
      keepPreviousData: true,
    }
  );
}
// const actions = {
//   addResponseComment: async (
//     responseId: string,
//     userId: string,
//     comment: string
//   ) => {
//     const res: any = await api.post(`responses/${responseId}/comments`, {
//       json: {
//         user_id: userId,
//         comment,
//       },
//     });
//   },
// };
const addResponseComment = async (props: any) => {
  const { response_id, user_id, comment } = props;
  const res: any = await api
    .post(`response/${response_id}/comments`, {
      json: {
        user_id,
        comment,
      },
    })
    .json();
  return res;
};
export const useAddResponseComment = () => {
  const queryClient = useQueryClient();
  return useMutation(addResponseComment, {
    onSuccess: (res) => {
      queryClient.invalidateQueries([cache.RESPONSE_COMMENTS, res.response_id]);
      // queryClient.invalidateQueries(cache.PROMPT_RESPONSES);
    },
  });
};

const deleteComment = async (props: any) => {
  const { comment_id, comment_type } = props;
  try {
    const DELETE_ROUTES = {
      responses: 'response_comments/delete',
      sonar: 'sonar_comments/delete',
      prompt: 'prompt_comments/delete',
    };
    const res: any = await api.delete(
      DELETE_ROUTES[comment_type as keyof typeof DELETE_ROUTES],
      {
        json: {
          comment_id,
        },
      }
    );

    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem deleting your comment.');
  }
};
export const useDeleteComment = (chatType: string) => {
  const queryClient = useQueryClient();
  return useMutation(deleteComment, {
    onSuccess: () => {
      if (chatType === 'sonar') {
        queryClient.refetchQueries([SONAR_CACHE_KEYS.GROUP_MESSAGES]);
      } else if (chatType === 'responses') {
        queryClient.refetchQueries([cache.RESPONSE_COMMENTS]);
      } else if (chatType === 'prompt') {
        queryClient.refetchQueries([PROMPT_CACHE.PROMPT_COMMENTS]);
      }
    },
  });
};

/* Responses */

export const getSignedUrl = async (
  promptId: string,
  ext: string,
  bucket: 'belouga' | 'belouga-uploads' = 'belouga-uploads'
) => {
  try {
    const res: any = await api.get(
      `user_responses/get-signed-url/${promptId}/${ext}/?bucket=${bucket}`
    );
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem submitting your response.');
  }
};

const submitResponse = async (props: any) => {
  const { prompt_id, response_type, text, ...rest } = props;
  try {
    const res: any = await api.post(`user_responses/${prompt_id}`, {
      json: {
        response_type,
        text,
        ...rest,
      },
    });

    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem submitting your response.');
  }
};

export const useSubmitResponse = (promptId: string) => {
  const queryClient = useQueryClient();
  return useMutation(submitResponse, {
    onSuccess: ({ user_id }) => {
      queryClient.invalidateQueries([cache.PROMPT_RESPONSES, promptId]);
      queryClient.refetchQueries([
        cache.USER_RESPONSES,
        { prompt_id: promptId, user_id },
      ]);
    },
  });
};

const deleteResponse = async (props: any) => {
  const { response_id } = props;
  try {
    const res: any = await api.delete('user_responses/delete', {
      json: {
        response_id,
      },
    });

    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem deleting your response.');
  }
};
export const useDeleteResponse = (userId: string, promptId: string) => {
  const queryClient = useQueryClient();
  return useMutation(deleteResponse, {
    onSuccess: () => {
      queryClient.invalidateQueries([cache.PROMPT_RESPONSES, promptId]);
      queryClient.refetchQueries([cache.USER_RESPONSES]);
    },
  });
};
// export const usePromptLike = () => {
//   const queryClient = useQueryClient();
//   return useMutation(togglePromptLike, {
//     onSuccess: (prompt) => {
//       queryClient.setQueryData([cache.PROMPT, prompt.id], prompt);
//     },
//   });
// };

// const addPromptComment = async (props: any) => {
//   // add try catch
//   console.log('adding comment', props);
//   const prompt = await actions.addPromptComment(props);
//   return prompt;
// };

// export const useAddPromptComment = () => {
//   const queryClient = useQueryClient();
//   return useMutation(addPromptComment, {
//     onSuccess: (prompt) => {
//       if (prompt) {
//         queryClient.setQueryData([cache.PROMPT, prompt.id], prompt);
//       }
//     },
//   });
// };
